import { useEffect, useMemo, type ReactNode } from 'react'
import { api } from '../api/client'
import { bulkMediaApi } from '../bulkMedia/api'
import { stageFile } from '../bulkMedia/stage'
import { useAuth } from '../context/AuthContext'
import { useToolAccess } from '../hooks/useToolAccess'
import { hasBulkMediaAccess } from '../permissions'
import { crmClient, crmMedia } from '../crm/client'
import { LibraryDepsProvider, type LibraryDeps, type VaultUploadApi } from './LibraryDeps'

/** The real Library dependencies for one account: the console's CRM client. */
export function AccountLibraryDeps({ accountId, children }: { accountId: string; children: ReactNode }) {
  // Upload is Bulk Media into this one Account (ADR 0031): launched for this login
  // and access_bulk_media here. The backend refuses the job otherwise.
  const { getPermissions, loadAccountPermissions } = useAuth()
  const launched = useToolAccess()?.launched.bulk_media ?? false
  const perms = getPermissions(accountId)
  useEffect(() => {
    if (launched && !perms) void loadAccountPermissions(accountId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, launched, perms])
  const uploads = launched && hasBulkMediaAccess(perms)
  // Only the Account may change `gateway` and the rest: pages page through OnlyFans
  // keyed on them, so a new `gateway` when permissions land (and Upload appears)
  // would throw away the first page in flight and ask again.
  const base = useMemo<LibraryDeps>(
    () => ({
      gateway: (path, body) => crmClient.gateway(accountId, path, body),
      loadMedia: (url, signal) => crmMedia.load(accountId, url, signal),
      streamMedia: (url) => crmMedia.stream(accountId, url),
      notes: {
        load: (ids) => api.getVaultNotes(accountId, ids),
        save: (id, text) => api.saveVaultNotes(accountId, id, text),
      },
      badges: {
        list: () => api.getVaultBadges(accountId),
        create: (name, color) => api.createVaultBadge(accountId, name, color),
        update: (id, patch) => api.updateVaultBadge(accountId, id, patch),
        remove: (id) => api.deleteVaultBadge(accountId, id),
        setItems: (id, add, remove) => api.setVaultBadgeItems(accountId, id, add, remove),
      },
    }),
    [accountId],
  )
  const deps = useMemo<LibraryDeps>(() => {
    if (!uploads) return base
    const upload: VaultUploadApi = {
      stage: (file, onProgress, signal) => stageFile(bulkMediaApi, file, { onProgress, signal }),
      start: (fileIds, category) =>
        bulkMediaApi.createJob({
          kind: 'upload',
          source: 'vault',
          account_ids: [accountId],
          category_names: category ? [category] : [],
          file_ids: fileIds,
        }),
      job: (id) => bulkMediaApi.job(id),
      retry: (id) => bulkMediaApi.retryJob(id),
    }
    return { ...base, upload }
  }, [base, accountId, uploads])
  return <LibraryDepsProvider value={deps}>{children}</LibraryDepsProvider>
}
